// src/components/DocumentsStepReview.tsx
/**
 * ==================================================================================
 * DOCUMENTS STEP REVIEW COMPONENT - FIXED
 * ==================================================================================
 * 
 * ✅ Individual document approval/rejection
 * ✅ Approve = simple click (no modal)
 * ✅ Reject = modal with rejection reason + admin notes
 * ✅ Admin notes displayed for ALL documents
 */

import React, { useState } from 'react';
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import apiClient from '../services/apiClient';
import { ImageCard } from '../pages/OnboardingDetailPage-part5';

interface Document {
  id: string;
  documentType: string;
  fileName: string;
  publicUrl?: string;
  status: string;
  adminReviewStatus: string;
  rejectionReason?: string;
  adminNotes?: string;
  uploadedAt: string;
  reviewedAt?: string;
  reviewedBy?: string;
}

interface DocumentsStepReviewProps {
  userId: string;
  documents: Document[];
  onUpdate: () => void;
}

const DOCUMENT_LABELS: Record<string, string> = {
  driver_license_front: 'Driver License (Front)',
  driver_license_back: 'Driver License (Back)',
  vehicle_insurance: 'Vehicle Insurance',
  vehicle_registration: 'Vehicle Registration',
  profile_photo: 'Profile Photo'
};

const DocumentsStepReview: React.FC<DocumentsStepReviewProps> = ({
  userId,
  documents,
  onUpdate
}) => {
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showConfirmApprove, setShowConfirmApprove] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // ✅ APPROVE - Opens inline confirm modal (no browser confirm/alert)
  const handleApproveClick = (document: Document) => {
    setSelectedDocument(document);
    setShowConfirmApprove(true);
  };

  const handleApproveConfirm = async () => {
    if (!selectedDocument) return;

    setProcessing(true);
    try {
      await apiClient.post(`/onboarding/documents/admin/${selectedDocument.id}/approve`);
      showToast(`${DOCUMENT_LABELS[selectedDocument.documentType]} approved successfully!`, 'success');
      setShowConfirmApprove(false);
      setSelectedDocument(null);
      onUpdate(); // Refresh the data
    } catch (error: any) {
      showToast(error.response?.data?.message || 'Failed to approve document', 'error');
    } finally {
      setProcessing(false);
    }
  };

  // ✅ REJECT - Opens modal with rejection reason + admin notes
  const openRejectModal = (document: Document) => {
    setSelectedDocument(document);
    setRejectionReason(document.rejectionReason || '');
    setAdminNotes(document.adminNotes || '');
    setShowRejectModal(true);
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      showToast('Please provide a rejection reason', 'error');
      return;
    }

    if (!selectedDocument) return;

    setProcessing(true);
    try {
      await apiClient.post(`/onboarding/documents/admin/${selectedDocument.id}/reject`, {
        reason: rejectionReason,
        notes: adminNotes || undefined
      });

      showToast(`${DOCUMENT_LABELS[selectedDocument.documentType]} rejected`, 'success');
      setShowRejectModal(false);
      setSelectedDocument(null);
      setRejectionReason('');
      setAdminNotes('');
      onUpdate(); // Refresh the data
    } catch (error: any) {
      showToast(error.response?.data?.message || 'Failed to reject document', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (doc: Document) => {
    if (doc.adminReviewStatus === 'approved') {
      return (
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '6px 12px',
          background: '#d1fae5',
          color: '#065f46',
          borderRadius: '8px',
          fontSize: '12px',
          fontWeight: '600'
        }}>
          <CheckCircle size={14} /> Approved
        </span>
      );
    } else if (doc.adminReviewStatus === 'rejected') {
      return (
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '6px 12px',
          background: '#fecaca',
          color: '#991b1b',
          borderRadius: '8px',
          fontSize: '12px',
          fontWeight: '600'
        }}>
          <XCircle size={14} /> Rejected
        </span>
      );
    } else {
      return (
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '6px 12px',
          background: '#fef3c7',
          color: '#92400e',
          borderRadius: '8px',
          fontSize: '12px',
          fontWeight: '600'
        }}>
          <AlertCircle size={14} /> Pending Review
        </span>
      );
    }
  };

  const requiredDocs = [
    'driver_license_front',
    'driver_license_back',
    'vehicle_insurance',
    'vehicle_registration',
    'profile_photo'
  ];

  return (
    <div style={{ padding: '20px 0' }}>
      <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '20px', color: '#0f172a' }}>
        Document Review - Individual Approval/Rejection
      </h4>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: '20px'
      }}>
        {requiredDocs.map((docType) => {
          const doc = documents.find(d => d.documentType === docType);

          return (
            <div
              key={docType}
              style={{
                background: '#f8fafc',
                borderRadius: '12px',
                border: '2px solid #e2e8f0',
                overflow: 'hidden',
                transition: 'all 0.2s'
              }}
            >
              {/* Header */}
              <div style={{
                padding: '16px',
                borderBottom: '2px solid #e2e8f0',
                background: 'white'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '8px'
                }}>
                  <h5 style={{
                    margin: 0,
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#0f172a'
                  }}>
                    {DOCUMENT_LABELS[docType]}
                  </h5>
                  {doc && getStatusBadge(doc)}
                </div>
              </div>

              {/* Content */}
              {doc ? (
                <>
                  {/* Image Preview - Using authenticated endpoint */}
                  <div style={{ height: '220px' }}>
                    <ImageCard
                      key={`${doc.id}-${doc.uploadedAt}`}
                      imagePath={`/onboarding/documents/${doc.id}/image`}
                      label={DOCUMENT_LABELS[doc.documentType] || doc.documentType}
                    />
                  </div>

                  {/* Document Info */}
                  <div style={{ padding: '12px 16px', background: 'white' }}>
                    <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px' }}>
                      Uploaded: {new Date(doc.uploadedAt).toLocaleDateString()}
                    </div>

                    {/* ✅ FIXED: Show admin notes/rejection for ALL documents */}
                    {(doc.rejectionReason || doc.adminNotes) && (
                      <div style={{
                        padding: '12px',
                        background: doc.adminReviewStatus === 'rejected' ? '#fee2e2' : '#f0f9ff',
                        borderRadius: '8px',
                        marginBottom: '12px',
                        border: `1px solid ${doc.adminReviewStatus === 'rejected' ? '#fca5a5' : '#bfdbfe'}`
                      }}>
                        {doc.rejectionReason && (
                          <>
                            <div style={{
                              fontSize: '11px',
                              fontWeight: '600',
                              color: doc.adminReviewStatus === 'rejected' ? '#991b1b' : '#1e40af',
                              marginBottom: '4px'
                            }}>
                              Rejection Reason:
                            </div>
                            <div style={{ 
                              fontSize: '12px', 
                              color: doc.adminReviewStatus === 'rejected' ? '#991b1b' : '#1e40af',
                              marginBottom: doc.adminNotes ? '8px' : '0'
                            }}>
                              {doc.rejectionReason}
                            </div>
                          </>
                        )}
                        
                        {doc.adminNotes && (
                          <>
                            <div style={{
                              fontSize: '11px',
                              fontWeight: '600',
                              color: doc.adminReviewStatus === 'rejected' ? '#7c2d12' : '#1e40af',
                              marginBottom: '4px'
                            }}>
                              Admin Notes:
                            </div>
                            <div style={{ 
                              fontSize: '12px', 
                              color: doc.adminReviewStatus === 'rejected' ? '#7c2d12' : '#1e40af'
                            }}>
                              {doc.adminNotes}
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div style={{ display: 'flex', gap: '8px' }}>

                      {/* ✅ Show Approve button if not already approved */}
                      {doc.adminReviewStatus !== 'approved' && (
                        <button
                          onClick={() => handleApproveClick(doc)}
                          disabled={processing}
                          style={{
                            flex: 1,
                            padding: '8px 12px',
                            background: '#d1fae5',
                            border: '2px solid #10b981',
                            borderRadius: '8px',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#065f46',
                            cursor: processing ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            opacity: processing ? 0.5 : 1
                          }}
                        >
                          <CheckCircle size={14} /> Approve
                        </button>
                      )}

                      {/* ✅ Always show Reject button (can re-reject if needed) */}
                      <button
                        onClick={() => openRejectModal(doc)}
                        disabled={processing}
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          background: '#fecaca',
                          border: '2px solid #ef4444',
                          borderRadius: '8px',
                          fontSize: '13px',
                          fontWeight: '600',
                          color: '#991b1b',
                          cursor: processing ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          opacity: processing ? 0.5 : 1
                        }}
                      >
                        <XCircle size={14} /> Reject
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div style={{
                  padding: '40px 20px',
                  textAlign: 'center',
                  color: '#94a3b8',
                  fontSize: '14px'
                }}>
                  📄 Not uploaded yet
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Toast Notification */}
      {toast && (
        <div style={{
          position: 'fixed',
          top: '80px',
          right: '24px',
          zIndex: 10001,
          padding: '14px 24px',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '600',
          color: 'white',
          background: toast.type === 'success'
            ? 'linear-gradient(135deg, #10b981, #059669)'
            : 'linear-gradient(135deg, #ef4444, #dc2626)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}>
          <span style={{ fontSize: '18px' }}>{toast.type === 'success' ? '✅' : '❌'}</span>
          {toast.message}
        </div>
      )}

      {/* Approve Confirmation Modal */}
      {showConfirmApprove && selectedDocument && (
        <div
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => { setShowConfirmApprove(false); setSelectedDocument(null); }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '16px',
              padding: '24px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>
              Approve Document
            </h3>
            <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: '#64748b' }}>
              Approve <strong>{DOCUMENT_LABELS[selectedDocument.documentType]}</strong>?
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowConfirmApprove(false); setSelectedDocument(null); }}
                disabled={processing}
                style={{
                  padding: '10px 20px',
                  background: 'white',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#6b7280',
                  cursor: processing ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleApproveConfirm}
                disabled={processing}
                style={{
                  padding: '10px 20px',
                  background: processing ? '#86efac' : '#10b981',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: 'white',
                  cursor: processing ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {processing ? 'Approving...' : (
                  <>
                    <CheckCircle size={16} /> Approve
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedDocument && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowRejectModal(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '16px',
              padding: '24px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{
              margin: '0 0 20px 0',
              fontSize: '18px',
              fontWeight: '700',
              color: '#0f172a'
            }}>
              Reject {DOCUMENT_LABELS[selectedDocument.documentType]}
            </h3>

            {/* Rejection Reason */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '8px'
              }}>
                Rejection Reason <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter reason for rejection (visible to driver)"
                rows={3}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            {/* Admin Notes */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '8px'
              }}>
                Admin Notes (Optional)
              </label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Internal notes (not visible to driver)"
                rows={2}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowRejectModal(false)}
                disabled={processing}
                style={{
                  padding: '10px 20px',
                  background: 'white',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#6b7280',
                  cursor: processing ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={processing || !rejectionReason.trim()}
                style={{
                  padding: '10px 20px',
                  background: processing || !rejectionReason.trim() ? '#fca5a5' : '#ef4444',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: 'white',
                  cursor: processing || !rejectionReason.trim() ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {processing ? 'Rejecting...' : (
                  <>
                    <XCircle size={16} /> Reject Document
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsStepReview;